import {
  BusinessEmployee,
  BusinessImprovementsDto,
  BusinessMaterial,
  GetBusinessBalanceResponse,
  GetBusinessInfoResponse,
  GetBusinessMaterialsResponse,
  GetBusinessOrdersResponse,
  GetBusinessProductsResponse,
  GetBusinessServiceStateResponse,
  GetMyBusinessesResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { DeliveryType } from "@1games-ru/region-shared/dist/enums/business/delivery";
import { EmployeeRole } from "@1games-ru/region-shared/dist/enums/business/employee";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "@/core/store";
import { BusinessSelectTab } from "@/domains/business/constants/businessSelectTab";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import { EmployeesTab } from "@/domains/business/constants/employeesTab";
import { ImprovementsTab } from "@/domains/business/constants/improvementsTab";
import { PurchaseHistoryTab } from "@/domains/business/constants/purchaseHistoryTab";
import { Tab } from "@/domains/business/constants/tab";

/* APP state */
export interface AppState {
  currentTab: Tab | null;
  tabs: Tab[];
  divice: DeviceEnum;
}

/* BUSINESS SELECT */
export interface BusinessSelectState {
  list: GetMyBusinessesResponse | [];
  ownerList: GetMyBusinessesResponse | [];
  employeeList: GetMyBusinessesResponse | [];
  tabs: BusinessSelectTab[] | [];
  tabsIcons: ((props: { className?: string }) => JSX.Element)[] | [];
  selectedTab: BusinessSelectTab | null;
}

/* INFO state */
export interface InfoState extends MyBusiness {
  // аналогично BusinessSelectState
}

/* GOODSANDWAREHOUSE state */
export interface CartItem extends BusinessMaterial {
  count: number;
}

export interface GoodsAndWarehouseState {
  data: GetBusinessMaterialsResponse | null;
  cart: {
    list: CartItem[] | null;
    deliveryType: DeliveryType;
  };
}

/* EMPLOYEES state */
export interface EmployeesState {
  employeesList: BusinessEmployee[];
  selectedEmployee: BusinessEmployee;
  tabs: EmployeesTab[];
  selectedTab: EmployeesTab;
  isDismissModal: boolean;
}

/* FEE state */
export interface FeeState {
  feeSelectedDays: number;
}

/* IMPROVEMENTS state */
export interface ImprovementsState {
  improvementsList: BusinessImprovementsDto[];
  tabs: ImprovementsTab[];
  selectedTab: ImprovementsTab;
  selectedImprovement: BusinessImprovementsDto | null;
}

/* SERVICE state */
export interface ServiceState extends GetBusinessServiceStateResponse {
  // businessId: number;
  // cleanPercent: number;
  // repairPercent: number;
  // cleaningPrice: number;
  // repairingPrice: number;
  // cleanEffect: number;
  // repairEffect: number;
}

/* PRICESFORSERVICES state */
export interface PricesForServiceState extends GetBusinessProductsResponse {
  // id: number;
  // price: number;
  // percent: number | null;
  // minPercent: number;
  // maxPercent: number;
  // pricedAt: Date | null;
}

export interface UpdatePricePayload {
  productId: number;
  percent: number;
  pricedAt: Date;
}

/* SAFE state */
export interface SafeState extends GetBusinessBalanceResponse {
  // businessId: number;
  // balance: number;
  // balanceLimit: number;
}

/* PURCHASE HISTORY state */
export interface PurchaseHistoryState {
  tabs: PurchaseHistoryTab[];
  selectedTab: PurchaseHistoryTab;
  data: GetBusinessOrdersResponse | null;
}

/* PC */
export interface PcState extends GetBusinessInfoResponse {}

/* STATE */
export interface BusinessState {
  app: AppState;
  businessSelect: BusinessSelectState;
  info: InfoState;
  goodsAndWarehouse: GoodsAndWarehouseState;
  employees: EmployeesState;
  fee: FeeState;
  improvements: ImprovementsState;
  service: ServiceState;
  pricesForServices: PricesForServiceState;
  safe: SafeState;
  purchaseHistory: PurchaseHistoryState;
  pc: PcState;
}

// ===================================================================

/* APP initial */
const initialApp: BusinessState["app"] = {
  currentTab: Tab.Info,
  tabs: [
    Tab.Info,
    Tab.GoodsAndWarehouse,
    Tab.Employees,
    Tab.Fee,
    Tab.Improvements,
    Tab.Service,
    Tab.PricesForServices,
    Tab.Safe,
    Tab.PurchaseHistory,
  ],
  divice: null,
};

/* BUSINESS SELECT initial */
const initialBusinessSelect: BusinessState["businessSelect"] = {
  list: [],
  ownerList: [],
  employeeList: [],
  tabs: [],
  tabsIcons: [],
  selectedTab: null,
};

/* INFO initial */
const initialInfo: BusinessState["info"] = null;

/* GOODSANDWAREHOUSE initial */
const initialGoodsAndWarehouse: BusinessState["goodsAndWarehouse"] = {
  data: null,
  cart: {
    list: null,
    deliveryType: DeliveryType.Auto,
  },
};

/* EMPLOYEES initial */
const initialEmployees: BusinessState["employees"] = {
  employeesList: null,
  selectedEmployee: null,
  tabs: [EmployeesTab.All, EmployeesTab.Managers, EmployeesTab.ServiceStaff],
  selectedTab: EmployeesTab.All,
  isDismissModal: false,
};

/* FEE initial */
const initialFee: BusinessState["fee"] = {
  feeSelectedDays: 0,
};

/* IMPROVEMENTS initial */
const initialImprovements: BusinessState["improvements"] = {
  improvementsList: null,
  tabs: [ImprovementsTab.All, ImprovementsTab.Standard, ImprovementsTab.Unique],
  selectedTab: ImprovementsTab.All,
  selectedImprovement: null,
};

/* SERVICE initial */
const initialService: BusinessState["service"] = null;

/* PRICESFORSERVICES initial */
const initialPricesForServices: BusinessState["pricesForServices"] = null;

/* SAFE initial */
const initialSafe: BusinessState["safe"] = null;

/* PURCHASE HISTORY initial */
const initialPurchaseHistory: BusinessState["purchaseHistory"] = {
  tabs: [PurchaseHistoryTab.All, PurchaseHistoryTab.Purchases, PurchaseHistoryTab.Expenses],
  selectedTab: PurchaseHistoryTab.All,
  data: null,
};

/* PC initial */
const initialPc: BusinessState["pc"] = null;

/* STATE initial */
const initialState: BusinessState = {
  app: initialApp,
  businessSelect: initialBusinessSelect,
  info: initialInfo,
  goodsAndWarehouse: initialGoodsAndWarehouse,
  employees: initialEmployees,
  fee: initialFee,
  improvements: initialImprovements,
  service: initialService,
  pricesForServices: initialPricesForServices,
  safe: initialSafe,
  purchaseHistory: initialPurchaseHistory,
  pc: initialPc,
};

// ===================================================================

const businessSlice = createSlice({
  name: "businessSlice",
  initialState,
  reducers: {
    /* STATE reducer */
    clearSlice: (state, action?: PayloadAction<"saveInfo">) => {
      state.app.currentTab = Tab.Info;
      state.app.divice = null;
      state.businessSelect = { ...initialBusinessSelect };
      state.fee.feeSelectedDays = 0;
      state.service = null;
      state.pricesForServices = null;
      state.employees.employeesList = [];
      state.employees.selectedEmployee = null;
      state.employees.selectedTab = EmployeesTab.All;
      state.employees.isDismissModal = false;
      state.improvements.improvementsList = null;
      state.improvements.selectedTab = ImprovementsTab.All;
      state.purchaseHistory.selectedTab = PurchaseHistoryTab.All;
      state.purchaseHistory.data = null;
      state.safe = null;
      state.goodsAndWarehouse = { ...initialGoodsAndWarehouse };
      state.pc = null;
      if (action.payload !== "saveInfo") {
        state.info = null;
      }
    },

    /* APP reducer */
    setCurrentTab: (state, action: PayloadAction<Tab>) => {
      state.app.currentTab = action.payload;
    },
    setDevice: (state, action: PayloadAction<DeviceEnum>) => {
      state.app.divice = action.payload;
    },

    /* BUSINESS SELECT reducer */
    setMyBusiness: (state, action: PayloadAction<GetMyBusinessesResponse>) => {
      state.businessSelect.list = action.payload;
    },
    setSelectOwnerBusinesses: (state, action: PayloadAction<GetMyBusinessesResponse>) => {
      state.businessSelect.ownerList = action.payload;
    },
    setSelectEmployeeBusinesses: (state, action: PayloadAction<GetMyBusinessesResponse>) => {
      state.businessSelect.employeeList = action.payload;
    },
    setSelectTabs: (state, action: PayloadAction<BusinessSelectTab[]>) => {
      state.businessSelect.tabs = action.payload;
    },
    setSelectTabsIcons: (
      state,
      action: PayloadAction<((props: { className?: string }) => JSX.Element)[]>,
    ) => {
      state.businessSelect.tabsIcons = action.payload;
    },
    setSelectSelectedTabs: (state, action: PayloadAction<BusinessSelectTab>) => {
      state.businessSelect.selectedTab = action.payload;
    },

    /* INFO reducer */
    setInfo: (state, action: PayloadAction<InfoState>) => {
      state.info = action.payload;
    },
    setFrozenImprovements: (state, action: PayloadAction<boolean>) => {
      state.info = { ...state.info, hasFrozenImprovements: action.payload };
    },
    setBusinessBalance: (state, action: PayloadAction<number>) => {
      if (state.info) {
        state.info.feeBalance = action.payload;
      }
    },

    /* GOODSANDWAREHOUSE reducer */
    setGoodsAndWarehouseData: (state, action: PayloadAction<GetBusinessMaterialsResponse>) => {
      state.goodsAndWarehouse.data = action.payload;
    },
    setGoodsAndWarehouseCartList: (state, action: PayloadAction<CartItem[]>) => {
      state.goodsAndWarehouse.cart.list = action.payload;
    },
    setGoodsAndWarehouseDeliveryType: (state, action: PayloadAction<DeliveryType>) => {
      state.goodsAndWarehouse.cart.deliveryType = action.payload;
    },

    /* EMPLOYEES reducer */
    setEmployeesList: (state, action: PayloadAction<BusinessEmployee[]>) => {
      state.employees.employeesList = action.payload;
    },
    setSelectedEmployee: (state, action: PayloadAction<BusinessEmployee>) => {
      state.employees.selectedEmployee = action.payload;
    },
    updateEmployee: (state, action: PayloadAction<{ id: number; newRole: EmployeeRole }>) => {
      state.employees.employeesList = state.employees.employeesList.map((item) => {
        if (item.characterId === action.payload.id) {
          return {
            ...item,
            role: action.payload.newRole,
          };
        }
        return item;
      });
    },
    removeEmployee: (state, action: PayloadAction<number>) => {
      state.employees.employeesList = state.employees.employeesList.filter(
        (item) => item.characterId !== action.payload,
      );
    },
    setSelectedEmployeesTab: (state, action: PayloadAction<EmployeesTab>) => {
      state.employees.selectedTab = action.payload;
    },
    setEmployeesIsDismissModal: (state, action: PayloadAction<boolean>) => {
      state.employees.isDismissModal = action.payload;
    },

    /* FEE reducer */
    setFeeSelectedDays: (state, action: PayloadAction<number>) => {
      state.fee.feeSelectedDays = action.payload;
    },

    /* IMPROVEMENTS reducer */
    setImprovementsList: (state, action: PayloadAction<BusinessImprovementsDto[] | null>) => {
      state.improvements.improvementsList = action.payload;
    },
    setSelectedImprovementsTab: (state, action: PayloadAction<ImprovementsTab>) => {
      state.improvements.selectedTab = action.payload;
    },
    setSelectedImprovement: (state, action: PayloadAction<BusinessImprovementsDto | null>) => {
      state.improvements.selectedImprovement = action.payload;
    },

    /* SERVICE reducer */
    setServiceInfo: (state, action: PayloadAction<ServiceState>) => {
      state.service = action.payload;
    },

    /* PRICESFORSERVICES reducer */
    setPricesForServicesList: (state, action: PayloadAction<PricesForServiceState>) => {
      state.pricesForServices = action.payload;
    },
    updatePricesForServiceList: (state, action: PayloadAction<UpdatePricePayload>) => {
      state.pricesForServices = state.pricesForServices.map((item) => {
        if (item.id !== action.payload.productId) {
          return item;
        }
        return { ...item, percent: action.payload.percent, pricedAt: action.payload.pricedAt };
      });
    },

    /* SAFE reducer */
    setSafeBalanceInfo: (state, action: PayloadAction<SafeState>) => {
      state.safe = action.payload;
    },

    /* PURCHASE HISTORY reducer */
    setSelectedPurchaseHistoryTab: (state, action: PayloadAction<PurchaseHistoryTab>) => {
      state.purchaseHistory.selectedTab = action.payload;
    },
    setPurchaseHistoryData: (state, action: PayloadAction<GetBusinessOrdersResponse>) => {
      state.purchaseHistory.data = action.payload;
    },

    /* PC reducer */
    setPcInfo: (state, action: PayloadAction<PcState>) => {
      state.pc = action.payload;
    },
    setBusinessPcBalance: (state, action: PayloadAction<number>) => {
      if (state.pc) {
        state.pc.feeBalance = action.payload;
      }
    },
  },
});

/* APP selector */
export const appCurrentTabSelector = (state: RootState) => state.business.app.currentTab;
export const appTabsSelector = (state: RootState) => state.business.app.tabs;
export const appDeviceSelector = (state: RootState) => state.business.app.divice;

/* BUSINESS SELECT selector */
export const businessSelectSelector = (state: RootState) => state.business.businessSelect.list;
export const businessSelectOwnerSelector = (state: RootState) =>
  state.business.businessSelect.ownerList;
export const businessSelectEmployeeSelector = (state: RootState) =>
  state.business.businessSelect.employeeList;
export const businessSelectTabsSelector = (state: RootState) => state.business.businessSelect.tabs;
export const businessSelectTabsIconsSelector = (state: RootState) =>
  state.business.businessSelect.tabsIcons;
export const businessSelectSelectedTabSelector = (state: RootState) =>
  state.business.businessSelect.selectedTab;

/* INFO selector */
export const infoSelector = (state: RootState) => state.business.info;

/* GOODSANDWAREHOUSE selector */
export const goodsAndWarehouseDataSelector = (state: RootState) =>
  state.business.goodsAndWarehouse.data;
export const goodsAndWarehouseCartListSelector = (state: RootState) =>
  state.business.goodsAndWarehouse.cart.list;
export const goodsAndWarehouseDeliveryTypeSelector = (state: RootState) =>
  state.business.goodsAndWarehouse.cart.deliveryType;

/* EMPLOYEES selector */
export const employeesListSelector = (state: RootState) => state.business.employees.employeesList;
export const employeesSelectedEmployeeSelector = (state: RootState) =>
  state.business.employees.selectedEmployee;
export const employeesCurrentTabSelector = (state: RootState) =>
  state.business.employees.selectedTab;
export const employeesTabsSelector = (state: RootState) => state.business.employees.tabs;
export const employeesIsDismissModalSelector = (state: RootState) =>
  state.business.employees.isDismissModal;

/* FEE selector */
export const feeSelectedDaysSelector = (state: RootState) => state.business.fee.feeSelectedDays;

/* IMPROVEMENTS selector */
export const improvementsListSelector = (state: RootState) =>
  state.business.improvements.improvementsList;
export const improvementsCurrentTabSelector = (state: RootState) =>
  state.business.improvements.selectedTab;
export const improvementsTabsSelector = (state: RootState) => state.business.improvements.tabs;
export const selectedImprovementSelector = (state: RootState) =>
  state.business.improvements.selectedImprovement;

/* SERVICE selector */
export const serviceInfoSelector = (state: RootState) => state.business.service;

/* PRICESFORSERVICES selector */
export const pricesForServicesSelector = (state: RootState) => state.business.pricesForServices;

/* SAFE selector */
export const safeBalanceInfoSelector = (state: RootState) => state.business.safe;

/* PURCHASE HISTORY selector */
export const purchaseHistoryCurrentTabSelector = (state: RootState) =>
  state.business.purchaseHistory.selectedTab;
export const purchaseHistoryTabsSelector = (state: RootState) =>
  state.business.purchaseHistory.tabs;
export const purchaseHistoryData = (state: RootState) => state.business.purchaseHistory.data;

/* PC selector */
export const pcInfoSelector = (state: RootState) => state.business.pc;

export const businessReducer = businessSlice.reducer;
export const {
  clearSlice,
  setCurrentTab,
  setDevice,
  setMyBusiness,
  setSelectOwnerBusinesses,
  setSelectEmployeeBusinesses,
  setSelectTabs,
  setSelectTabsIcons,
  setSelectSelectedTabs,
  setInfo,
  setFrozenImprovements,
  setBusinessBalance,
  setGoodsAndWarehouseData,
  setGoodsAndWarehouseCartList,
  setEmployeesList,
  setSelectedEmployee,
  updateEmployee,
  removeEmployee,
  setSelectedEmployeesTab,
  setEmployeesIsDismissModal,
  setFeeSelectedDays,
  setImprovementsList,
  setSelectedImprovementsTab,
  setSelectedImprovement,
  setServiceInfo,
  setPricesForServicesList,
  updatePricesForServiceList,
  setSafeBalanceInfo,
  setSelectedPurchaseHistoryTab,
  setPurchaseHistoryData,
  setPcInfo,
  setBusinessPcBalance,
} = businessSlice.actions;
